import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { ApiError } from '../api/client'
import type { SchemaResponse, TableInfo } from '../api/types'

export function SchemaExplorer() {
  const { apiClient } = useAuth()
  const [schema, setSchema] = useState<SchemaResponse | null>(null)
  const [selectedTable, setSelectedTable] = useState<TableInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tablesExpanded, setTablesExpanded] = useState(true)
  const [viewsExpanded, setViewsExpanded] = useState(true)

  useEffect(() => {
    if (!apiClient) return

    const client = apiClient
    async function loadSchema() {
      try {
        setLoading(true)
        setError(null)
        const data = await client.getSchema()
        setSchema(data)
        if (data.tables.length > 0) {
          setSelectedTable(data.tables[0])
        }
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message)
        } else {
          setError('Failed to load schema')
        }
      } finally {
        setLoading(false)
      }
    }

    loadSchema()
  }, [apiClient])

  if (loading) {
    return <div className="loading">Loading schema...</div>
  }

  if (error) {
    return (
      <div className="container">
        <div className="error">{error}</div>
      </div>
    )
  }

  if (!schema) {
    return null
  }

  return (
    <div className="container">
      <h2>Schema Explorer</h2>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '280px 1fr',
        gap: 'var(--spacing-lg)',
        marginTop: 'var(--spacing-lg)',
      }}>
        {/* Table list sidebar */}
        <div>
          <div className="card" style={{ padding: 0 }}>
            <button
              onClick={() => setTablesExpanded(!tablesExpanded)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                padding: 'var(--spacing-md)',
                borderBottom: '1px solid var(--color-border)',
                background: 'transparent',
                cursor: 'pointer',
                color: 'var(--color-text-primary)',
              }}
            >
              <h3 style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                Tables ({schema.tables.length})
              </h3>
              <span style={{
                transform: tablesExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform var(--transition-fast)',
              }}>
                ▼
              </span>
            </button>
            {tablesExpanded && (
              <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                {schema.tables.map((table) => (
                  <button
                    key={table.tableName}
                    onClick={() => setSelectedTable(table)}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: 'var(--spacing-sm) var(--spacing-md)',
                      textAlign: 'left',
                      background: selectedTable?.tableName === table.tableName
                        ? 'var(--color-bg-tertiary)'
                        : 'transparent',
                      border: 'none',
                      borderBottom: '1px solid var(--color-border-muted)',
                      color: 'var(--color-text-primary)',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontFamily: 'var(--font-family-mono)' }}>
                      {table.tableName}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                      {table.columns.length} columns
                      {table.foreignKeys.length > 0 && ` · ${table.foreignKeys.length} FKs`}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {schema.views.length > 0 && (
              <>
                <button
                  onClick={() => setViewsExpanded(!viewsExpanded)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    padding: 'var(--spacing-md)',
                    borderBottom: '1px solid var(--color-border)',
                    borderTop: '1px solid var(--color-border)',
                    background: 'transparent',
                    cursor: 'pointer',
                    color: 'var(--color-text-primary)',
                  }}
                >
                  <h3 style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                    Views ({schema.views.length})
                  </h3>
                  <span style={{
                    transform: viewsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform var(--transition-fast)',
                  }}>
                    ▼
                  </span>
                </button>
                {viewsExpanded && (
                  <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                    {schema.views.map((view) => (
                      <Link
                        key={view}
                        to={`/tables/${view}`}
                        style={{
                          display: 'block',
                          padding: 'var(--spacing-sm) var(--spacing-md)',
                          borderBottom: '1px solid var(--color-border-muted)',
                          fontFamily: 'var(--font-family-mono)',
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--color-text-secondary)',
                          textDecoration: 'none',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'var(--color-bg-tertiary)';
                          e.currentTarget.style.color = 'var(--color-text-primary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = 'var(--color-text-secondary)';
                        }}
                      >
                        {view}
                      </Link>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Table detail panel */}
        <div>
          {selectedTable && (
            <div className="card">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--spacing-md)',
              }}>
                <h3 style={{
                  margin: 0,
                  fontFamily: 'var(--font-family-mono)',
                }}>
                  {selectedTable.tableName}
                </h3>
                <Link
                  to={`/tables/${selectedTable.tableName}`}
                  className="btn btn-secondary"
                >
                  Browse Data
                </Link>
              </div>

              {/* Columns */}
              <h4 style={{ marginTop: 'var(--spacing-lg)', marginBottom: 'var(--spacing-sm)' }}>
                Columns
              </h4>
              <div style={{ overflow: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Column</th>
                      <th>Type</th>
                      <th>Nullable</th>
                      <th>Default</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTable.columns.map((col) => (
                      <tr key={col.columnName}>
                        <td>
                          <span style={{ fontFamily: 'var(--font-family-mono)' }}>
                            {col.columnName}
                          </span>
                          {col.isPrimaryKey && (
                            <span className="badge badge-primary" style={{ marginLeft: 'var(--spacing-sm)' }}>
                              PK
                            </span>
                          )}
                        </td>
                        <td>
                          <span className="mono text-secondary">{col.dataType}</span>
                        </td>
                        <td>
                          {col.isNullable ? (
                            <span className="text-muted">Yes</span>
                          ) : (
                            <span className="text-secondary">No</span>
                          )}
                        </td>
                        <td>
                          {col.columnDefault ? (
                            <span className="mono text-muted" style={{ fontSize: 'var(--font-size-xs)' }}>
                              {col.columnDefault.length > 30
                                ? col.columnDefault.slice(0, 30) + '...'
                                : col.columnDefault}
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Foreign Keys */}
              {selectedTable.foreignKeys.length > 0 && (
                <>
                  <h4 style={{ marginTop: 'var(--spacing-lg)', marginBottom: 'var(--spacing-sm)' }}>
                    Foreign Keys
                  </h4>
                  <div style={{ overflow: 'auto' }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Column</th>
                          <th>References</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedTable.foreignKeys.map((fk) => (
                          <tr key={fk.constraintName}>
                            <td>
                              <span className="mono">{fk.columnName}</span>
                            </td>
                            <td>
                              <button
                                onClick={() => {
                                  const table = schema.tables.find(t => t.tableName === fk.referencedTable)
                                  if (table) setSelectedTable(table)
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: 'var(--color-accent)',
                                  cursor: 'pointer',
                                  padding: 0,
                                  fontFamily: 'var(--font-family-mono)',
                                }}
                              >
                                {fk.referencedTable}.{fk.referencedColumn}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
